import { Link } from "react-router-dom";
import { FileText, Layers, Video, Zap, ArrowDown, Check } from "lucide-react";
import { SEOHead, howToJsonLd } from "@/components/seo/SEOHead.tsx";

const T = {
  ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df",
  paper: "#faf8f3", paper2: "#f3efe5", accent: "#ff5b1f",
};

const STEPS = [
  {
    num: "01",
    icon: FileText,
    title: "Generate & review your script",
    time: "~30 seconds",
    description:
      "Choose a channel preset (or start from scratch), add optional guidance like a specific topic or tone, and hit Generate. In under 30 seconds you'll have a structured script broken into a hook, body sections, and call-to-action. Edit any section inline — change wording, rewrite paragraphs, or regenerate with a different tone. Approve when you're happy.",
    details: [
      "Preset-based or freeform topic selection",
      "Full inline editing — every word is yours to change",
      "Tone presets: punchier, shorter, longer, casual, serious",
      "Word count and estimated video duration shown live",
      "Nothing advances until you click Approve",
    ],
  },
  {
    num: "02",
    icon: Layers,
    title: "Review the storyboard",
    time: "1–2 minutes",
    description:
      "Once the script is approved, TubeForge generates an image for every scene — one per script segment. Images appear in real time via live streaming. Click any frame to edit its prompt and regenerate. Bulk-select multiple frames to regenerate in one go. Choose your caption font and color. When every frame looks right, hit Approve.",
    details: [
      "One AI-generated image per script segment",
      "Frames appear live as they're generated (SSE)",
      "Click any frame to edit its AI prompt",
      "Bulk regeneration for multiple frames",
      "Caption font and color picker",
      "Storyboard approval gate — nothing renders without your OK",
    ],
  },
  {
    num: "03",
    icon: Video,
    title: "Render, download, or publish",
    time: "2–5 minutes",
    description:
      "After storyboard approval, TubeForge assembles the final video automatically: AI voiceover is recorded and synced to each scene, captions are aligned word-by-word, and background music is mixed in. Watch progress in real time with a stage-by-stage tracker. When it's done, download the MP4 or publish directly to YouTube.",
    details: [
      "AI voiceover via OpenAI or ElevenLabs",
      "Word-level caption alignment with Whisper",
      "Background music auto-mixed",
      "Live render progress with ETA",
      "Download MP4 on every plan",
      "One-click YouTube publish (Pro & Agency)",
    ],
  },
];

export function HowItWorksPage() {
  return (
    <>
      <SEOHead
        title="How AI Faceless Video Creation Works"
        description="From topic to published video in 3 steps: generate a script, review the storyboard, and render."
        path="/how-it-works"
        jsonLd={howToJsonLd({
          name: "How to Create a Faceless YouTube Video with AI",
          description: "Create a publish-ready faceless YouTube video in a few steps using TubeForge's AI workflow.",
          steps: [
            { name: "Generate your script", text: "Enter your channel niche, topic, and tone. The AI writes a structured script with a hook, body sections, and call-to-action in about 30 seconds." },
            { name: "Review the storyboard", text: "Approve the script and TubeForge generates one AI image per scene. Edit any frame's prompt and regenerate until the visuals match your vision." },
            { name: "Render and publish", text: "Approve the storyboard. TubeForge assembles the voiceover, captions, and music automatically. Download the MP4 or publish directly to YouTube." },
          ],
        })}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <p className="text-[13px] font-semibold uppercase tracking-widest mb-4" style={{ color: T.accent }}>
            How it works
          </p>
          <h1
            className="text-3xl md:text-5xl font-bold leading-tight mb-5"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.03em" }}
          >
            How AI faceless video creation <span style={{ color: T.accent }}>actually works</span>
          </h1>
          <p className="text-[16px] md:text-[18px] leading-relaxed max-w-xl mx-auto" style={{ color: T.inkSoft }}>
            You stay in control at every gate. Nothing happens without your approval.
            From topic to video in a few steps.
          </p>
        </div>
      </section>

      {/* Steps */}
      {STEPS.map((step, i) => {
        const Icon = step.icon;
        const isLast = i === STEPS.length - 1;
        return (
          <section
            key={step.num}
            className="py-16 md:py-20"
            style={{ backgroundColor: i % 2 === 0 ? T.paper2 : T.paper }}
          >
            <div className="mx-auto max-w-5xl px-5 md:px-8">
              {/* Step header */}
              <div className="flex items-center gap-5 mb-8">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: T.accent }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <span
                    className="text-[12px] font-mono font-semibold tracking-wide"
                    style={{ color: T.accent }}
                  >
                    STEP {step.num}
                  </span>
                  <h2
                    className="text-2xl md:text-3xl font-bold"
                    style={{
                      fontFamily: '"Bricolage Grotesque", "Inter", system-ui',
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {step.title}
                  </h2>
                </div>
                <span
                  className="hidden md:inline-block ml-auto text-[13px] font-mono px-3 py-1.5 rounded-md"
                  style={{ backgroundColor: "rgba(255,91,31,0.08)", color: T.accent }}
                >
                  {step.time}
                </span>
              </div>

              {/* Description + details */}
              <div className="flex flex-col md:flex-row gap-10 md:gap-16">
                <div className="flex-1">
                  <p className="text-[15px] leading-relaxed" style={{ color: T.inkSoft }}>
                    {step.description}
                  </p>
                </div>
                <div className="flex-1">
                  <ul className="space-y-3">
                    {step.details.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-[14px]" style={{ color: T.ink }}>
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: T.accent }} />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Arrow connector */}
              {!isLast && (
                <div className="flex justify-center mt-12">
                  <ArrowDown className="w-6 h-6" style={{ color: T.rule }} />
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Timeline summary */}
      <section className="py-12 md:py-16" style={{ backgroundColor: T.paper2, borderTop: `1px solid ${T.rule}` }}>
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h3 className="text-[18px] font-bold mb-1">From topic to video in a few steps</h3>
              <p className="text-[14px]" style={{ color: T.inkSoft }}>
                Script, storyboard, render: done in a few steps.
              </p>
            </div>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 text-[15px] font-semibold px-7 py-3.5 rounded-xl transition-opacity hover:opacity-90 flex-shrink-0"
              style={{ backgroundColor: T.accent, color: "#fff" }}
            >
              <Zap className="w-4 h-4" /> Try it free
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
